import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {
  OrderDetailsSection_Item,
  OrderDetailsSection_Package,
  OrderDetailsSection_Packages,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/order_success_page/v1/sections.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  TrackProduct,
  useTrackFeatureInViewImpression,
} from '@aboutyou/tracking';
import {
  COLORS,
  FONT_SIZES,
  Image,
  MultipleItemsUnitPrice,
  PriceBox,
  ProductImage,
  Text,
  UnstyledLink,
  desktopAndUpLinaria,
  mobileOnlyLinaria,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';

const DeliveryItemDetail = (props: {
  label: string;
  value: string;
  'data-testid'?: string;
}) => {
  const {label, value} = props;

  return (
    <DeliveryItemDetailWrapper data-testid={props['data-testid']}>
      <DeliveryItemDetailLabel size="px12">{label}</DeliveryItemDetailLabel>
      <Text size="px12" weight="mediumHeavy">
        {value}
      </Text>
    </DeliveryItemDetailWrapper>
  );
};

const DeliveryItem = (props: {
  item: OrderDetailsSection_Item;
  index: number;
}) => {
  const {item} = props;
  const {isActive: isPriceDisplayOverhaulV2Active} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );

  const {t} = useTranslation();

  return (
    <StyledTrackProduct
      productTile={{
        product: item.productTracker,
        brand: item.brandTracker,
        price: item.totalPrice,
      }}
      streamPosition={props.index}
      triggerOnce
      badges={[]}
    >
      <DeliveryItemWrapperContent
        data-testid={'ospBasketProduct-' + item.productTracker.productId}
      >
        <DeliveryItemImageWrapper>
          <UnstyledLink to={item.productLink.url} aria-label={undefined}>
            <StyledProductImage
              src={item.productImage.src}
              padded={item.productImage.isEnclosed}
              widths={[120, 220, 360]}
            />
          </UnstyledLink>
        </DeliveryItemImageWrapper>
        <DeliveryItemInfoWrapper>
          <div>
            <ProductNameBrandWrapper>
              <ProductBrandText
                weight="mediumHeavy"
                color="Grey0"
                data-testid="productBrandName"
              >
                {item.brandLabel}
              </ProductBrandText>
              <ProductBrandText color="Grey0" data-testid="productName">
                {item.productLabel}
              </ProductBrandText>
            </ProductNameBrandWrapper>
            <DeliveryItemDetailsList>
              <DeliveryItemDetail
                label={t('Size:')}
                value={item.sizeLabel}
                data-testid="productSize"
              />
              <DeliveryItemDetail
                label={t('Color:')}
                value={item.colorLabel}
                data-testid="productColor"
              />
              <DeliveryItemDetail
                label={t('Quantity:')}
                value={item.quantity.toString()}
                data-testid="productQuantity"
              />
            </DeliveryItemDetailsList>
          </div>
          <DeliveryItemPriceWrapper>
            <PriceBox
              align="right"
              price={
                isPriceDisplayOverhaulV2Active
                  ? item.totalPriceV2
                  : item.totalPrice
              }
              hideVat
              productId={item.productTracker.productId}
            />
            {!isPriceDisplayOverhaulV2Active && item.quantity > 1 && (
              <StyledMultiplePrice unitPrice={item.unitPrice} />
            )}
          </DeliveryItemPriceWrapper>
        </DeliveryItemInfoWrapper>
      </DeliveryItemWrapperContent>
    </StyledTrackProduct>
  );
};

const Delivery = (props: {pkg: OrderDetailsSection_Package}) => {
  const {pkg} = props;

  return (
    <DeliveryWrapper data-testid="ospDeliverySection">
      <DeliveryHeadlineWrapper data-testid="ospDeliveryDetails">
        <Text size="px16" weight="mediumHeavy">
          {pkg.label}
        </Text>
        <CarrierLogoDeliveryTimeWrapper>
          <CarrierLogo
            src={pkg.carrierLogo.url}
            breakpoints={[]}
            ratio={'5:4'}
          />
          <DeliveryDateBadgeWrapper>
            <DeliveryDateBadge>
              <Text size="px12">{pkg.expectedDeliveryDate}</Text>
            </DeliveryDateBadge>
          </DeliveryDateBadgeWrapper>
        </CarrierLogoDeliveryTimeWrapper>
      </DeliveryHeadlineWrapper>
      <DeliverItemList data-testid="ospDeliveryPackagesContainer">
        {pkg.items.map((item, i) => (
          <DeliveryItem key={i} item={item} index={i} />
        ))}
      </DeliverItemList>
    </DeliveryWrapper>
  );
};

const Deliveries = (props: {packages: OrderDetailsSection_Package[]}) => {
  const {packages} = props;

  return (
    <DeliveriesWrapper>
      {packages.map((pkg, i) => (
        <Delivery key={i} pkg={pkg} />
      ))}
    </DeliveriesWrapper>
  );
};

export const DeliveryOverview = (props: {
  packages: OrderDetailsSection_Packages;
}) => {
  const {packages} = props;

  const {ref} = useTrackFeatureInViewImpression<HTMLDivElement>({
    label: 'delivery_section',
    name: 'delivery_section',
  });

  return (
    <DeliverOverviewWrapper ref={ref}>
      <DeliveryOverviewHeadline size="px18" weight="mediumHeavy">
        {packages.label}
      </DeliveryOverviewHeadline>
      <Deliveries packages={packages.packages} />
    </DeliverOverviewWrapper>
  );
};

const ProductNameBrandWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledMultiplePrice = styled(MultipleItemsUnitPrice)`
  color: ${COLORS.strikePriceGrey};
`;

const DeliveryOverviewHeadline = styled(Text)`
  ${tabletAndUpLinaria(`
    display: none;
  `)}
`;

const CarrierLogoDeliveryTimeWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;

  ${tabletAndUpLinaria(`
    flex: unset;
    flex-direction: row-reverse;
    gap: 0.625rem;
  `)}
`;

const DeliveryItemPriceWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  flex: 1;
  flex-direction: column;
`;

const ProductBrandText = styled(Text)`
  line-height: 1.125rem;
  font-size: ${FONT_SIZES.px12};
  text-transform: uppercase;

  ${desktopAndUpLinaria(`
    font-size: ${FONT_SIZES.px14};
  `)}
`;

const StyledTrackProduct = styled(TrackProduct)``;

const DeliveryItemWrapperContent = styled.div`
  display: flex;
  overflow: hidden;
`;

const DeliverItemList = styled.div`
  display: flex;
  flex-direction: column;

  & > ${StyledTrackProduct} {
    border: 1px solid ${COLORS.Grey3};
    border-bottom: unset;
  }
  & > ${StyledTrackProduct}:first-of-type {
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
  }
  & > ${StyledTrackProduct}:last-of-type {
    border-bottom-left-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
    border-bottom: 1px solid ${COLORS.Grey3};

    ${tabletAndUpLinaria(`
      border-bottom: unset;
    `)}
  }
  & > ${StyledTrackProduct} {
    ${tabletAndUpLinaria(`
      border: unset;
      border-bottom: unset;
      border-radius: unset;
    `)}
  }

  ${tabletAndUpLinaria(`
    gap: 1.5rem;
  `)}
`;

const DeliveryItemImageWrapper = styled.div`
  flex: 1.8;

  ${tabletAndUpLinaria(`
    flex: unset;
  `)}
`;

const StyledProductImage = styled(ProductImage)`
  ${mobileOnlyLinaria(`
    border-radius: unset;
  `)}

  ${tabletAndUpLinaria(`
    width: 8.125rem;
  `)}
`;

const DeliveryItemInfoWrapper = styled.div`
  flex: 2;
  padding: 1rem 1rem 0.75rem;
  display: flex;
  flex-direction: column;

  ${tabletAndUpLinaria(`
    padding: 0.5rem 0 0 2rem;
    flex-direction: row;

  `)}
`;

const DeliveryItemDetailWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const DeliveryItemDetailLabel = styled(Text)`
  width: 4rem;

  ${desktopAndUpLinaria(`
    width: 6.25rem;
  `)}
`;

const DeliveryItemDetailsList = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.125rem;
  margin-top: 0.5rem;
`;

const DeliverOverviewWrapper = styled.div`
  padding: 0.5rem 1rem 1rem;
  display: flex;
  gap: 1rem;
  flex-direction: column;

  ${tabletAndUpLinaria(`
    padding: 0;
  `)}
`;

const DeliveriesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const DeliveryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  ${tabletAndUpLinaria(`
    padding-top: 1rem;

    &:not(:first-of-type) {
      border-top: 1px solid ${COLORS.Grey3};
    }
  `)}
`;

const DeliveryHeadlineWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const CarrierLogo = styled(Image)`
  height: 1.625rem;
  width: auto;
`;

const DeliveryDateBadgeWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
`;

const DeliveryDateBadge = styled.div`
  border-radius: 3.125rem;
  display: flex;
  height: 1.625rem;
  padding: 0 0.75rem;
  align-items: center;
  justify-content: center;
  background-color: ${COLORS.Grey4};
  line-height: 1.125rem;
`;
